import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { zodResolver } from "@hookform/resolvers/zod";
import { DefaultValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { StoreProjectAssigneeUpdate } from "@/types/storeProjectAssignees";
import PageHeader from "@/components/PageHeader";
import { useStoreProjectAssigneeBreadcrumbs } from "@/modules/storeProjectAssignees/hooks/useStoreProjectAssigneeBreadcrumbs";
import { storeProjectAssigneeUpdate } from "@/schemas/storeProjectAssignees";
import { useCallback, useEffect, useMemo } from "react";
import Form from "@/components/Form";
import PageContent from "@/components/PageContent";
import SaveButton from "@/components/SaveButton";
import { isRequired } from "@/lib/form";
import CheckboxField from "@/components/CheckboxField";
import UserSelect from "@/components/UserSelect";
import Field from "@/components/Field";
import { useStoreProjectAssignee } from "@/modules/storeProjectAssignees/hooks/useStoreProjectAssignee";
import { useUpdateStoreProjectAssignee } from "@/modules/storeProjectAssignees/hooks/useUpdateStoreProjectAssignee";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import Spinner from "@/components/Spinner";
import ProjectSelect from "@/components/ProjectSelect";
import ProjectStoreSelect from "@/modules/projects/components/ProjectStoreSelect";
import { DeleteButton } from "@/components/DeleteButton";
import { useDeleteStoreProjectAssignee } from "@/modules/storeProjectAssignees/hooks/useDeleteStoreProjectAssignee";
import { Module } from "@/constants";
import { SystemRoleCodesType } from "@/types/user";
import { SUPPORTED_SYSTEM_ROLE_CODES } from "@/modules/defaultStoreProjectAssignees/constants";
import { isEmptyArray } from "@/lib/utils/isEmptyArray";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { CheckedState } from "@radix-ui/react-checkbox";

const defaultValues: DefaultValues<StoreProjectAssigneeUpdate> = {
  store_id: "",
  project_id: "",
  is_primary: false,
  user_id: "",
  selectedSystemRoleCodes: [],
};

export default function StoreProjectAssigneesEditor() {
  const { t } = useTranslation();
  const deleteMutation = useDeleteStoreProjectAssignee();
  const { data, isLoading } = useStoreProjectAssignee(
    !deleteMutation.isPending && !deleteMutation.isSuccess,
  );
  const mutation = useUpdateStoreProjectAssignee();
  const { can_update, can_delete } = useModulePermissions(
    Module.StoreProjectAssignees,
  );
  const breadcrumbItems = useStoreProjectAssigneeBreadcrumbs([
    {
      label: data?.user_name ?? "...",
    },
  ]);
  const isDisabled =
    deleteMutation.isPending || mutation.isPending || isLoading;

  const form = useForm<StoreProjectAssigneeUpdate>({
    resolver: zodResolver(storeProjectAssigneeUpdate),
    defaultValues,
    disabled: isDisabled,
  });

  const projectId = form.watch("project_id");
  const selectedSystemRoleCodes = form.watch("selectedSystemRoleCodes");

  const supportedRoles = useMemo(
    () =>
      SUPPORTED_SYSTEM_ROLE_CODES.map((systemRoleCode) => ({
        value: systemRoleCode,
        label: t(`userRole.${systemRoleCode}`),
      })),
    [t],
  );

  const handleRoleChange = useCallback(
    (role: SystemRoleCodesType, checked: CheckedState) => {
      const current = form.getValues(
        "selectedSystemRoleCodes",
      ) as SystemRoleCodesType[];
      let updated: SystemRoleCodesType[];
      if (checked) {
        updated = current.includes(role) ? current : [...current, role];
      } else {
        updated = current.filter((r) => r !== role);
      }
      form.setValue("selectedSystemRoleCodes", updated, {
        shouldValidate: false,
      });
    },
    [form],
  );

  // Calculate initial values for selectedSystemRoleCodes
  useEffect(() => {
    if (!data) return;

    let selectedRoles: SystemRoleCodesType[] = [];
    if (data.user_role_code) {
      const currentRole = data.user_role_code as SystemRoleCodesType;
      if (SUPPORTED_SYSTEM_ROLE_CODES.includes(currentRole)) {
        selectedRoles = [currentRole];
      }
    }

    form.reset({
      ...data,
      selectedSystemRoleCodes: selectedRoles,
    });
  }, [data?.id, form]);

  useFormUnsavedChangesWarning(form);

  const handleSubmit = useCallback(
    async (formData: StoreProjectAssigneeUpdate) => {
      const { selectedSystemRoleCodes, ...data } = formData;
      await mutation.mutateAsync(data);
    },
    [mutation],
  );

  const handleDelete = useCallback(async () => {
    if (!data?.id) return;
    await deleteMutation.mutateAsync(data.id);
  }, [deleteMutation, data]);

  if (isLoading) {
    return (
      <Spinner
        label={t("storeProjectAssignees.loading")}
        className="w-full h-full"
      />
    );
  }

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbItems} />
      <Form form={form} handleSubmit={handleSubmit}>
        <PageContent
          title={t("storeProjectAssignees.title.edit")}
          footer={
            <div className="flex justify-end gap-2">
              <DeleteButton
                onClick={handleDelete}
                disabled={isDisabled || !can_delete}
              />
              <SaveButton disabled={isDisabled || !can_update} />
            </div>
          }
        >
          <div className="flex flex-row gap-4 mt-6">
            <div className="text-sm">
              {t("storeProjectAssignees.title.role")}
            </div>
            {supportedRoles.map((role) => (
              <Label
                className="flex items-center gap-2 font-normal"
                key={role.value}
              >
                <Checkbox
                  value={role.value}
                  onCheckedChange={(checked) =>
                    handleRoleChange(role.value, checked)
                  }
                  checked={
                    selectedSystemRoleCodes?.includes(role.value) ?? false
                  }
                />
                {role.label}
              </Label>
            ))}
          </div>

          <div className="flex flex-row gap-4 my-6 items-center">
            <Field
              name="user_id"
              label={t("storeProjectAssignees.fields.user_name")}
              required={isRequired(storeProjectAssigneeUpdate.shape.user_id)}
              render={({ field }) => (
                <UserSelect
                  {...field}
                  className="w-80"
                  defaultItem={{ id: data?.user_id, name: data?.user_name }}
                  systemRoleCodes={selectedSystemRoleCodes ?? []}
                  disabled={
                    field.disabled || isEmptyArray(selectedSystemRoleCodes)
                  }
                  showRole={true}
                />
              )}
            />
            <CheckboxField
              name="is_primary"
              label={t("storeProjectAssignees.fields.is_primary")}
              required={isRequired(storeProjectAssigneeUpdate.shape.is_primary)}
              disabled={isDisabled}
            />
          </div>
          <div className="flex flex-row gap-4 my-6">
            <Field
              name="project_id"
              label={t("storeProjectAssignees.fields.project_name")}
              required={isRequired(storeProjectAssigneeUpdate.shape.project_id)}
              render={({ field }) => (
                <ProjectSelect
                  {...field}
                  className="w-80"
                  defaultItem={{
                    id: data?.project_id,
                    name: data?.project_name,
                  }}
                />
              )}
            />
            <Field
              name="store_id"
              label={t("storeProjectAssignees.fields.store_name")}
              required={isRequired(storeProjectAssigneeUpdate.shape.store_id)}
              render={({ field }) => (
                <ProjectStoreSelect
                  {...field}
                  className="w-80"
                  defaultItem={{ id: data?.store_id, name: data?.store_name }}
                  projectId={projectId}
                  disabled={!projectId}
                />
              )}
            />
          </div>
        </PageContent>
      </Form>
    </>
  );
}
